import { supabase } from '@common/api/authService';
import {
    useCallback, useEffect, useState
} from 'react';
import { Permission } from './permissions';
import { WorkspaceRole } from './useWorkspaces';

/**
 * Role Permission 数据结构
 */
export interface RolePermission {
    role: WorkspaceRole;
    permission: string;
    allowed: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Hook 返回类型
 */
export interface UsePermissionsReturn {
    isLoading: boolean;
    error: Error | null;
    checkPermissions: (permissions: Permission[]) => boolean;
    refresh: () => Promise<void>;
}

/**
 * 获取角色的权限
 * 根据当前用户的 workspace_role，从 role_permissions 表中获取对应的权限
 */
export const usePermissions = (role: WorkspaceRole | null): UsePermissionsReturn => {
    const [permissions, setPermissions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    /**
     * 从数据库拉取角色的权限
     */
    const fetchPermissions = useCallback(async () => {
        if (!role) {
            setPermissions([]);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {

            const { data, error } = await supabase
                .from('role_permissions')
                .select('permission')
                .eq('role', role)
                .eq('allowed', true);

            if (error) {
                throw error;
            }

            // 提取权限字符串列表
            const permissionList = (data || []).map((p: { permission: any }) => p.permission);
            setPermissions(permissionList);

        } catch (err: any) {
            setError(err);
            setPermissions([]);
        } finally {
            setIsLoading(false);
        }
    }, [role]);

    // 初始化和 role 变化时拉取权限
    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    /**
     * 检查是否有所有指定的权限
     * @param permissionList - 要检查的权限列表
     * @returns 是否拥有所有权限
     */
    const checkPermissions = useCallback((permissionList: Permission[]): boolean => {
        return permissionList.every(permission => permissions.includes(permission));
    }, [permissions]);


    return {
        isLoading,
        error,
        checkPermissions,
        refresh: fetchPermissions,
    };
};
